import axios from '@/libs/axios'

export default {
  namespaced: true,

  state: {
    sale: null,
    sales: [],
    salesWithPayments: [],
    salesWithPaymentsTotal: 0,
    mySales: [],
    mySalesWithPayments: [],
    charters: [],
  },

  getters: {
    sale(state) {
      return state.sale
    },
    sales(state) {
      return state.sales
    },
    salesWithPayments(state) {
      return state.salesWithPayments
    },
    salesWithPaymentsTotal(state) {
      return state.salesWithPaymentsTotal
    },
    mySales(state) {
      return state.mySales
    },
    mySalesWithPayments(state) {
      return state.mySalesWithPayments
    },
    charters(state) {
      return state.charters;
    },
  },

  mutations: {
    ADD_SALE(state, value) {
      state.sales.push(value);
    },
    SET_SALES(state, value) {
      state.sales = value
    },
    SET_SALE(state, value) {
      state.sale = value
    },
    SET_SALES_WITH_PAYMENTS(state, { data, total }) {
      state.salesWithPayments = data;
      state.sales = data;
      state.salesWithPaymentsTotal = total;
    },
    SET_MY_SALES(state, value) {
      state.mySales = value
    },
    SET_MY_SALES_WITH_PAYMENTS(state, value) {
      state.mySalesWithPayments = value;
      state.mySales = value;
    },
    SET_CHARTERS(state, value) {
      state.charters = value
    },
    UPDATE_SALES(state, changedSale) {
      const index = state.sales.findIndex(sale => sale.id === changedSale.id);
      if (index === -1) {
        return
      }
      Object.assign(state.sales[index], changedSale)
      if (changedSale.cancelled) {
        state.sales[index].payment_status = 1
      }
    },
    UPDATE_MY_SALES(state, changedSale) {
      const index = state.mySales.findIndex(sale => sale.id === changedSale.id)
      if (index === -1) {
        return
      }
      Object.assign(state.mySales[index], changedSale)
      if (changedSale.cancelled) {
        state.mySales[index].payment_status = 1
      }
    },
    DELETE_SALES(state, changedSaleId) {
      const index = state.sales.findIndex(sale => sale.id === changedSaleId)
      if (index === -1) {
        return
      }
      state.sales.splice(index, 1)
      if (state.salesWithPaymentsTotal > 0) {
        state.salesWithPaymentsTotal -= 1
      }
    },
  },

  actions: {
    loadSales({ commit }) {
      return axios.get('api/sales').then(response => {
        commit('SET_SALES', response.data.data)
      })
    },
    loadNotCruiseSalesWithPayments({ commit }) {
      return axios.get('api/notCruiseSalesWithPayments').then(response => {
        const data = response.data.data || []
        commit('SET_SALES_WITH_PAYMENTS', { data, total: data.length })
      })
    },
    loadSalesWithPayments({ commit }, {
      userId,
      repeats,
      withReturns,
      page = 1,
      perPage = 10,
      filters = {},
    } = {}) {
      const url = repeats ? 'api/salesWithPayments/repeats' : (userId == null ? 'api/salesWithPayments' : 'api/salesWithPaymentsByUser/' + userId)
      const params = {
        page,
        per_page: perPage,
        ...filters,
      }

      if (withReturns) {
        params.with_returns = 1
      }

      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key]
        }
      })

      return axios.get(url, { params })
          .then(response => {
            const data = response.data.data || []
            const total = Number(response.data.meta?.total ?? data.length)
            commit('SET_SALES_WITH_PAYMENTS', { data, total })
            return { data, total, meta: response.data.meta }
          });
    },
    loadCruiseSalesWithPayments({ commit }) {
      return axios.get('api/cruiseSalesWithPayments').then(response => {
        const data = response.data.data || []
        commit('SET_SALES_WITH_PAYMENTS', { data, total: data.length })
      })
    },
    loadInshorePackagesWithPayments({ commit }) {
      return axios.get('api/inshorePackagesWithPayments').then(response => {
        const data = response.data.data || []
        commit('SET_SALES_WITH_PAYMENTS', { data, total: data.length })
      })
    },
    loadMySales({ commit }) {
      return axios.get('api/my-sales').then(response => {
        commit('SET_MY_SALES', response.data.data)
      })
    },
    loadMySalesWithPayments({ commit }) {
      return axios.get('api/my-sales-with-payments').then(response => {
        commit('SET_MY_SALES_WITH_PAYMENTS', response.data.data)
      })
    },
    loadCharters({ commit }) {
      return axios.get('api/charters').then(response => {
        commit('SET_CHARTERS', response.data.data)
      })
    },
    createSale(_, data) {
      return axios.post('api/sales', data)
    },
    payAndCreateSale(_, data) {
      return axios.post('api/sale/create', data)
    },
    createSaleManually(_, data) {
      return axios.post('api/create-sale', data)
    },
    updateSale({ commit }, data) {
      return axios.put(`api/sales/${data.id}`, data).then(response => {
        commit('UPDATE_SALES', response.data.data);
        return response.data;
      })
    },
    addSalePayment({ commit }, data) {
      return axios.post('api/write-payment-history', data).then(response => {
        if (response.data.data) {
          commit('UPDATE_SALES', response.data.data)
        }
        return response.data
      })
    },
    refundSalePayment({ commit }, data) {
      return axios.post('api/refund-payment', data).then(response => {
        if (response.data.data) {
          commit('UPDATE_SALES', response.data.data)
        }
        return response.data
      })
    },
    updateMySale({ commit }, data) {
      return axios.put(`api/sales/${data.id}`, data).then(() => {
        commit('UPDATE_MY_SALES', data)
      })
    },
    saleDetails({ commit }, id) {
      return axios.get(`api/sale-details/${id}`).then((response) => {
        const data = response.data.data ?? response.data
        commit('SET_SALE', data)
        return data
      })
    },
    sale({ commit }, id) {
      return axios.get(`api/sales/${id}`).then((response) => {
        const data = response.data.data ?? response.data
        commit('SET_SALE', data)
        return data
      })
    },
    makeSalePayment({ commit }, data) {
      return axios.post('api/make-payment', data).then((response) => {
        commit('UPDATE_MY_SALES', response.data.data);
        return response.data;
      })
    },
    deleteSale({ commit }, id) {
      return axios.delete(`api/sales/${id}`).then(() => {
        commit('DELETE_SALES', id)
      })
    },
    resendEmail({ commit }, data) {
      return axios.post(`api/sale-email-resend/${data.id}`, data).then(response => {
        return response.data;
      })
    },
    sendInvoice({ commit }, data) {
      return axios.post(`api/sale-invoice-send/${data.id}`, data).then(response => {
        return response.data;
      })
    },
    printInvoice(_, saleId) {
      return axios.get(`api/sales/${saleId}/invoice/pdf`, {
        responseType: 'blob',
      })
    },
    repeat({ commit }, data) {
      return axios.post(`api/sale-repeat/${data.id}`, data).then(response => {
        commit('ADD_SALE', response.data.data)
        return response.data;
      })
    },
  },
}
